import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import coverPic from "../../assets/images/Hrms.png";
import Loader from '../../common/spinner';
import { postData, getData, putData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';

const inputStyle = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        borderRadius: '4px',
        color: '#000',
        '& fieldset': { borderColor: '#ccc' },
        '&:hover fieldset': { borderColor: '#1a237e' },
        '&.Mui-focused fieldset': { borderColor: '#1a237e' },
    },
    '& .MuiInputBase-input::placeholder': {
        color: '#666',
        opacity: 1,
    }
};

const buttonStyle = {
    bgcolor: "#1a237e",
    fontWeight: "bold",
    "&:hover": { bgcolor: "#0d47a1" },
    "&:disabled": { bgcolor: "#344195", color: "#ffffffaa" },
    textTransform: 'none',
    height: '40px'
};

// Backend error/message shapes are inconsistent — sometimes a plain string,
// sometimes { msg: "..." } (rate limiter) or { error: "..." } (500s).
const extractMessage = (msg, fallback) => {
    if (!msg) return fallback;
    if (typeof msg === 'string') return msg;
    return msg.msg || msg.message || msg.error || fallback;
};

const EmailVerification = () => {
    const { token } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryToken = queryParams.get('token');

    const actualToken = token || queryToken;

    const navigate = useNavigate();
    const [step, setStep] = useState('verifying'); // verifying | email | otp | success | error
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('Verifying your email...');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (!actualToken) {
            setMessage('Invalid verification link');
            setStep('error');
            return;
        }

        const verifyEmailToken = async () => {
            try {
                const response = await getData(API_ENDPOINTS.verifyEmailToken(actualToken));

                if (response.status === "SUCCESS") {
                    setStep('verified');
                    setMessage('Email verified successfully! Redirecting to OTP page...');

                    try {
                        const tokenParts = actualToken.split('.');
                        if (tokenParts.length === 3) {
                            const payload = JSON.parse(atob(tokenParts[1]));
                            if (payload.email) {
                                setEmail(payload.email);
                                setTimeout(() => {
                                    setStep('otp');
                                    requestOTP(payload.email);
                                }, 2000);
                            } else {
                                setStep('email');
                                setMessage('Please enter your email to continue');
                            }
                        } else {
                            setStep('email');
                            setMessage('Please enter your email to continue');
                        }
                    } catch (error) {
                        setStep('email');
                        setMessage('Please enter your email to continue');
                    }
                } else {
                    setMessage(extractMessage(response.message, 'Email verification failed. The link may be invalid or expired.'));
                    setStep('error');
                }
            } catch (error) {
                setMessage(extractMessage(error.response?.data?.message, 'Email verification failed. The link may be invalid or expired.'));
                setStep('error');
            }
        };

        verifyEmailToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualToken]);

    const handleEmailSubmit = (e) => {
        e.preventDefault();
        if (email) {
            requestOTP(email);
        } else {
            setMessage('Please enter a valid email');
        }
    };

    const requestOTP = async (emailToUse) => {
        setIsSubmitting(true);
        setMessage('Sending OTP...');
        try {
            const response = await postData(API_ENDPOINTS.generateVerificationOtp, {
                email: emailToUse || email
            });

            if (response.status === "SUCCESS") {
                setStep('otp');
                setMessage('OTP has been sent to your email');
            } else {
                setMessage(extractMessage(response.message, 'Failed to send OTP'));
                setStep('error');
            }
        } catch (error) {
            setMessage(extractMessage(error.response?.data?.message, 'An error occurred while requesting OTP'));
            setStep('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const verifyOTPAndSetPassword = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setMessage("Passwords don't match!");
            setStep('error');
            return;
        }

        if (password.length < 6) {
            setMessage("Password must be at least 6 characters long");
            setStep('error');
            return;
        }

        setIsSubmitting(true);
        setMessage('Verifying OTP and setting password...');

        try {
            const verifyResponse = await postData(API_ENDPOINTS.verifyOtp, { email, otp });

            if (verifyResponse.status === "SUCCESS") {
                const updateResponse = await putData(API_ENDPOINTS.resetUserPassword, {
                    email,
                    newPassword: password,
                    confirmPassword
                });

                if (updateResponse.status === "SUCCESS") {
                    setStep('success');
                    setMessage('Password set successfully! Redirecting to login...');
                    setTimeout(() => navigate('/login'), 3000);
                } else {
                    setMessage(extractMessage(updateResponse.message, 'Failed to update password'));
                    setStep('error');
                }
            } else {
                setMessage(extractMessage(verifyResponse.message, 'Invalid OTP'));
                setStep('error');
            }
        } catch (error) {
            setMessage(extractMessage(error.response?.data?.message, 'An error occurred during verification'));
            setStep('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resendOTP = (emailToResend) => {
        requestOTP(emailToResend);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, height: "100vh", bgcolor: "#292929", position: "relative" }}>

            {isSubmitting && <Loader />}

            <Box sx={{ flex: 1, backgroundImage: `url(${coverPic})`, backgroundSize: "cover", backgroundPosition: "center" }} />

            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                <Box sx={{ width: "90%", maxWidth: "350px", textAlign: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, color: "#fff" }}>
                        Email Verification
                    </Typography>

                    {message && (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                textAlign: "center",
                                color: step === 'success' ? '#69f0ae' : step === 'error' ? '#ff6b6b' : '#bbb',
                                bgcolor: step === 'success' ? 'rgba(105,240,174,0.1)' : step === 'error' ? 'rgba(255,107,107,0.1)' : 'transparent',
                                p: step === 'error' || step === 'success' ? 1 : 0,
                                borderRadius: 1,
                            }}
                        >
                            {message}
                        </Typography>
                    )}

                    {step === 'verifying' && (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <CircularProgress sx={{ color: "#fff", mb: 2 }} />
                        </Box>
                    )}

                    {step === 'email' && (
                        <form onSubmit={handleEmailSubmit}>
                            <TextField
                                fullWidth
                                size="small"
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                sx={inputStyle}
                            />
                            <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={buttonStyle}>
                                Continue
                            </Button>
                        </form>
                    )}

                    {step === 'otp' && (
                        <form onSubmit={verifyOTPAndSetPassword}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter 6-digit OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                inputProps={{ maxLength: 6 }}
                                required
                                sx={inputStyle}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="New password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                sx={inputStyle}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(v => !v)} edge="end" sx={{ color: "#666" }}>
                                                {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                sx={inputStyle}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(v => !v)} edge="end" sx={{ color: "#666" }}>
                                                {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={buttonStyle}>
                                Verify & Set Password
                            </Button>
                            <Typography variant="caption" sx={{ mt: 2, color: '#bbb', display: 'block' }}>
                                Didn't receive OTP?{' '}
                                <Button
                                    onClick={() => resendOTP(email)}
                                    disabled={isSubmitting}
                                    sx={{ textTransform: 'none', color: '#8c9eff', minWidth: 0, p: 0, verticalAlign: 'baseline' }}
                                >
                                    Resend OTP
                                </Button>
                            </Typography>
                        </form>
                    )}

                    {step === 'error' && (
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => navigate('/login')}
                            sx={buttonStyle}
                        >
                            Back to Login
                        </Button>
                    )}

                    {step === 'success' && (
                        <Typography variant="h4" sx={{ color: '#69f0ae' }}>✓</Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default EmailVerification;
